/**
 * Extra diagnostic signals for the per-visit AI analysis.
 *
 * Everything here is privacy-safe: we record WHICH field / request / message,
 * never what the visitor typed. Each signal is capped per page load so a
 * broken page can't flood the analytics table.
 */
import { scrubUrl } from "@/lib/client-errors/scrub";

type TrackFn = (
  type: "custom",
  opts?: { name?: string; path?: string; value?: number; props?: Record<string, unknown> },
) => void;

const clean = (s: string | null | undefined, max = 80) =>
  (s ?? "").replace(/\s+/g, " ").trim().slice(0, max);

/** Generated ids (Radix `radix-:r1:`, React `:r5:` / `_r_s_`) mean nothing to a human. */
const isGeneratedId = (v: string) => /^(radix-|:r|_r_|«r)|^[:_«]?r[0-9a-z]+[:_»]?$/i.test(v) || /[:«»]/.test(v);

/**
 * Human-readable name of a form control: explicit data-track-field, then a
 * meaningful name/autocomplete, then its visible label, then aria/placeholder.
 */
export function describeField(el: HTMLElement): string {
  const attr = (n: string) => el.getAttribute(n) ?? "";
  const explicit = attr("data-track-field");
  if (explicit) return clean(explicit, 60);
  const name = attr("name");
  if (name && !isGeneratedId(name)) return clean(name, 60);
  const aria = attr("aria-label");
  if (aria) return clean(aria, 60);
  const labelledBy = attr("aria-labelledby");
  if (labelledBy) {
    const t = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent ?? "")
      .join(" ");
    if (clean(t)) return clean(t, 60);
  }
  const id = attr("id");
  if (id) {
    try {
      const lab = document.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (lab && clean(lab.textContent)) return clean(lab.textContent, 60);
    } catch {
      /* ignore */
    }
  }
  const wrap = el.closest("label");
  if (wrap && clean(wrap.textContent)) return clean(wrap.textContent, 60);
  // Label sitting just above the control inside the same wrapper div.
  const prev = el.parentElement?.querySelector("label");
  if (prev && clean(prev.textContent)) return clean(prev.textContent, 60);
  const ac = attr("autocomplete");
  if (ac && ac !== "off" && ac !== "on") return ac;
  const ph = attr("placeholder");
  if (ph) return clean(ph, 60);
  if (id && !isGeneratedId(id)) return clean(id, 60);
  const type = attr("type");
  return `${el.tagName.toLowerCase()}${type ? `[${type}]` : ""}`;
}

/** `/_serverFn/<base64>` ids encode the function — decode to something readable. */
export function describeRequest(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const m = u.pathname.match(/\/_serverFn\/([^/?#]+)/);
    if (m) {
      const raw = decodeURIComponent(m[1]);
      try {
        const json = JSON.parse(atob(raw.replace(/-/g, "+").replace(/_/g, "/")));
        const fn = json?.export ?? json?.functionId ?? json?.name;
        const file = String(json?.file ?? "").split("/").pop();
        if (fn) return `serverFn ${file ? `${file}#` : ""}${String(fn).replace(/_createServerFn_handler$/, "")}`;
      } catch {
        /* not base64 json */
      }
      const readable = raw.match(/([A-Za-z]+)_createServerFn/)?.[1];
      return `serverFn ${readable ?? raw.slice(0, 40)}`;
    }
    return scrubUrl(u.pathname).slice(0, 120);
  } catch {
    return url.slice(0, 120);
  }
}

const NOISY_CONSOLE = /download the react devtools|\[vite\]|\[hmr\]|lovable|favicon|ResizeObserver loop/i;

export function installDiagnostics(opts: { track: TrackFn; endpoint: string; formPaths: RegExp }) {
  if (typeof window === "undefined") return;
  const { track, endpoint, formPaths } = opts;
  const budget = new Map<string, number>();
  const allow = (key: string, max: number) => {
    const n = budget.get(key) ?? 0;
    if (n >= max) return false;
    budget.set(key, n + 1);
    return true;
  };

  // ── 1. Native validation bubbles ("Please fill out this field") ───────────
  window.addEventListener(
    "invalid",
    (e) => {
      const el = e.target as HTMLInputElement | null;
      if (!el || (el as any).type === "password" || !allow("invalid", 30)) return;
      track("custom", {
        name: "field_invalid",
        props: { field: describeField(el), message: clean(el.validationMessage, 160) },
      });
    },
    { capture: true },
  );

  // ── 2. Visible error messages: inline form errors + error toasts ──────────
  const ERROR_SELECTOR = [
    "[role=alert]",
    "[data-form-error]",
    "[aria-invalid=true]",
    "[data-sonner-toast][data-type=error]",
    "p.text-rose-600",
    "p.text-destructive",
    "div.text-rose-800",
  ].join(",");
  const seen = new Set<string>();
  let scanTimer: number | undefined;
  const scan = () => {
    scanTimer = undefined;
    const isForm = formPaths.test(window.location.pathname);
    document.querySelectorAll<HTMLElement>(ERROR_SELECTOR).forEach((el) => {
      const isToast = el.hasAttribute("data-sonner-toast");
      if (!isForm && !isToast) return;
      if (el.offsetParent === null && !isToast) return; // hidden
      let text: string;
      let field: string | undefined;
      if (el.getAttribute("aria-invalid") === "true") {
        field = describeField(el);
        const desc = (el.getAttribute("aria-describedby") ?? "")
          .split(/\s+/)
          .map((id) => document.getElementById(id)?.textContent ?? "")
          .join(" ");
        text = clean(desc, 200) || "marked invalid";
      } else {
        text = clean(el.textContent, 200);
        const ctl = el.parentElement?.querySelector<HTMLElement>("input,select,textarea,button[role=combobox]");
        if (ctl) field = describeField(ctl);
      }
      if (!text) return;
      const key = `${window.location.pathname}|${field ?? ""}|${text}`;
      if (seen.has(key) || !allow("form_error", 40)) return;
      seen.add(key);
      track("custom", {
        name: isToast ? "error_toast" : "form_error",
        props: { message: text, field },
      });
    });
  };
  try {
    new MutationObserver(() => {
      if (scanTimer == null) scanTimer = window.setTimeout(scan, 500);
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-invalid", "role", "data-type"],
    });
  } catch {
    /* ignore */
  }

  // ── 3. Failed / slow requests to our own server ───────────────────────────
  const origFetch = window.fetch;
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
    let sameOrigin = false;
    try {
      sameOrigin = new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
      /* ignore */
    }
    if (!sameOrigin || url.includes(endpoint) || /client-error|\/api\/public\/geo/.test(url)) {
      return origFetch.call(this, input as RequestInfo, init);
    }
    const started = performance.now();
    const method = (init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase();
    try {
      const res = await origFetch.call(this, input as RequestInfo, init);
      const ms = Math.round(performance.now() - started);
      if (res.status >= 400 && allow("net_error", 20)) {
        track("custom", { name: "net_error", value: res.status, props: { request: `${method} ${describeRequest(url)}`, status: res.status, ms } });
      } else if (ms > 4000 && allow("net_slow", 15)) {
        track("custom", { name: "net_slow", value: ms, props: { request: `${method} ${describeRequest(url)}`, status: res.status, ms } });
      }
      return res;
    } catch (err) {
      const aborted = (err as Error)?.name === "AbortError";
      if (!aborted && allow("net_error", 20)) {
        track("custom", {
          name: "net_error",
          value: 0,
          props: {
            request: `${method} ${describeRequest(url)}`,
            error: clean((err as Error)?.message, 120),
            online: navigator.onLine,
            ms: Math.round(performance.now() - started),
          },
        });
      }
      throw err;
    }
  };

  // ── 4. Console errors (not every one becomes a reported exception) ────────
  const origErr = console.error;
  const consoleSeen = new Set<string>();
  console.error = function (...args: unknown[]) {
    try {
      const msg = clean(args.map((a) => (a instanceof Error ? a.message : typeof a === "string" ? a : "")).join(" "), 200);
      if (msg && !NOISY_CONSOLE.test(msg) && !consoleSeen.has(msg) && allow("console", 10)) {
        consoleSeen.add(msg);
        track("custom", { name: "console_error", props: { message: msg } });
      }
    } catch {
      /* never break logging */
    }
    return origErr.apply(this, args as []);
  };

  // ── 5. Connection drops ───────────────────────────────────────────────────
  window.addEventListener("offline", () => allow("offline", 5) && track("custom", { name: "went_offline" }));
  window.addEventListener("online", () => allow("online", 5) && track("custom", { name: "back_online" }));

  // ── 6. Browser auto-translation (explains foreign button texts) ───────────
  const checkTranslated = () => {
    const html = document.documentElement;
    const translated =
      html.classList.contains("translated-ltr") ||
      html.classList.contains("translated-rtl") ||
      !!document.querySelector("font[style*='vertical-align: inherit']");
    if (translated && allow("translated", 1)) {
      track("custom", {
        name: "page_translated",
        props: { to: html.getAttribute("lang") ?? navigator.language, browser_lang: navigator.language },
      });
    }
  };
  try {
    new MutationObserver(checkTranslated).observe(document.documentElement, { attributes: true, attributeFilter: ["class", "lang"] });
  } catch {
    /* ignore */
  }
  window.setTimeout(checkTranslated, 4000);

  // ── 7. Copied text on catalogue pages (part numbers being compared) ───────
  document.addEventListener("copy", () => {
    if (!/^\/(p|shop|blog)(\/|$)/.test(window.location.pathname)) return;
    const sel = clean(window.getSelection()?.toString(), 60);
    if (!sel || /@/.test(sel) || !allow("copy", 10)) return;
    track("custom", { name: "copied_text", props: { text: sel } });
  });

  // ── 8. Slow first load ────────────────────────────────────────────────────
  window.addEventListener("load", () => {
    window.setTimeout(() => {
      const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      if (!nav) return;
      const load = Math.round(nav.loadEventEnd || nav.domComplete);
      if (load < 4000) return;
      track("custom", {
        name: "slow_page_load",
        value: load,
        props: {
          ttfb_ms: Math.round(nav.responseStart),
          dom_ready_ms: Math.round(nav.domContentLoadedEventEnd),
          load_ms: load,
          connection: (navigator as any).connection?.effectiveType,
        },
      });
    }, 0);
  });
}
